import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntil, catchError, of } from 'rxjs';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { SMEService } from '../../services/sme.service';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import {
  CRFMatch,
  CRFAppliedLead,
  ApplyCRFLeadRequest,
  CRFProductRange,
  CRFProductDetails,
  CRFPartnerDetails,
} from '../../models/crf-match.model';

@Component({
  selector: 'app-external-matches',
  templateUrl: './external-matches.component.html',
  styleUrls: ['./external-matches.component.scss'],
  standalone: true,
  imports: [CommonModule, PopupComponent],
})
export class ExternalMatchesComponent extends DestroyBase implements OnInit {
  private smeService = inject(SMEService);
  private commonService = inject(CommonService);
  private router = inject(Router);

  /** When set by a parent, overrides the internal activeTab (used to avoid nested tab bars). */
  @Input() set forcedTab(val: 'matches' | 'applied' | null) {
    if (val) {
      this.activeTab = val;
    }
  }

  @Output() tabChange = new EventEmitter<'matches' | 'applied'>();
  @Output() countsChange = new EventEmitter<{
    matches: number;
    applied: number;
  }>();

  activeTab: 'matches' | 'applied' = 'matches';

  isLoadingMatches = false;
  isLoadingApplied = false;

  currentSubmissionId: string | null = null;
  matches: CRFMatch[] = [];
  appliedLeads: CRFAppliedLead[] = [];
  appliedProductIds = new Set<string>();
  applyingProductId: string | null = null;
  failedImageProductIds = new Set<string>();
  failedAppliedImageIds = new Set<number>();
  selectedMatchForDetails: CRFMatch | null = null;

  ngOnInit(): void {
    this.loadMatches();
    this.loadAppliedLeads();
  }

  setTab(tab: 'matches' | 'applied'): void {
    this.activeTab = tab;
    this.tabChange.emit(tab);
  }

  emitCounts(): void {
    this.countsChange.emit({
      matches: this.matches.length,
      applied: this.appliedLeads.length,
    });
  }

  loadMatches(): void {
    this.isLoadingMatches = true;
    this.smeService
      .getCRFMatches()
      .pipe(
        takeUntil(this.destroy$),
        catchError((err) => {
          console.error('Error loading CRF matches:', err);
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to load external matches.',
          });
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.isLoadingMatches = false;
        if (res) {
          this.currentSubmissionId = res.submission_id ?? null;
          this.matches = Array.isArray(res.matches) ? res.matches : [];
        } else {
          this.matches = [];
        }
        this.emitCounts();
      });
  }

  loadAppliedLeads(): void {
    this.isLoadingApplied = true;
    this.smeService
      .getAppliedCRFLeads()
      .pipe(
        takeUntil(this.destroy$),
        catchError((err) => {
          console.error('Error loading applied leads:', err);
          return of([]);
        }),
      )
      .subscribe((leads) => {
        this.isLoadingApplied = false;
        this.appliedLeads = Array.isArray(leads) ? leads : [];
        this.appliedLeads.forEach((lead) => {
          if (lead.product_id) {
            this.appliedProductIds.add(String(lead.product_id));
          }
        });
        this.emitCounts();
      });
  }

  applyProduct(match: CRFMatch): void {
    const productId = this.getProductId(match);
    if (
      !productId ||
      productId === '-' ||
      this.appliedProductIds.has(productId) ||
      !this.currentSubmissionId
    ) {
      return;
    }

    if (this.applyingProductId) {
      return;
    }

    this.applyingProductId = productId;

    const payload: ApplyCRFLeadRequest = {
      product_id: productId,
      submission_id: this.currentSubmissionId,
    };

    this.smeService
      .applyCRFLead(payload)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err) => {
          console.error('Error applying CRF lead:', err);
          this.applyingProductId = null;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message:
              err?.error?.error ||
              'Failed to apply for product. Please try again.',
          });
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.applyingProductId = null;
        if (res && res.success) {
          this.appliedProductIds.add(productId);
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: `Lead applied successfully for ${this.getProductName(match)}!`,
          });
          this.loadAppliedLeads();
        }
      });
  }

  isProductApplied(match: CRFMatch): boolean {
    const id = this.getProductId(match);
    return id !== '-' && this.appliedProductIds.has(id);
  }

  isOlderSubmission(lead: CRFAppliedLead): boolean {
    return (
      !!this.currentSubmissionId &&
      !!lead.submission_id &&
      lead.submission_id !== this.currentSubmissionId
    );
  }

  getProductId(match: CRFMatch): string {
    return match.productId ? String(match.productId) : '-';
  }

  getProductName(match: CRFMatch): string {
    return this.cleanProductName(match.details?.name) || 'Loan Product';
  }

  getLenderName(match: CRFMatch): string {
    return match.partner?.name || 'CRF Partner';
  }

  getPartnerInitials(name: string): string {
    if (!name) {
      return 'P';
    }
    const clean = name.trim();
    const words = clean.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
    return clean.slice(0, 2).toUpperCase();
  }

  getProductKind(match: CRFMatch): string {
    return match.details?.kind || match.details?.loanType || 'Loan';
  }

  getProductImage(match: CRFMatch): string | undefined {
    const id = this.getProductId(match);
    if (id && this.failedImageProductIds.has(id)) {
      return undefined;
    }
    return match.details?.productImage;
  }

  onProductImageError(match: CRFMatch): void {
    const id = this.getProductId(match);
    if (id && id !== '-') {
      this.failedImageProductIds.add(id);
    }
  }

  getProductTerms(match: CRFMatch): string {
    const terms = match.details?.termsRange;
    if (terms && (terms.min !== undefined || terms.max !== undefined)) {
      return `${terms.min ?? ''}-${terms.max ?? ''} ${terms.unit || ''}`.trim();
    }
    return '';
  }

  getProductRates(match: CRFMatch): string {
    const rates = match.details?.interestRateRange;
    if (rates && (rates.min !== undefined || rates.max !== undefined)) {
      return `${rates.min ?? ''}% - ${rates.max ?? ''}% ${rates.unit ? '(' + rates.unit + ')' : ''}`.trim();
    }
    return '';
  }

  getAppliedProductName(lead: CRFAppliedLead): string {
    return this.cleanProductName(lead.product_details?.name) || 'Loan Product';
  }

  cleanProductName(name: string | undefined | null): string {
    if (!name || typeof name !== 'string') return '';
    const trimmed = name.trim();
    const lower = trimmed.toLowerCase();

    const knownMap: Record<string, string> = {
      loans_capital: 'Loans & Capital',
      'loans-capital': 'Loans & Capital',
      working_capital: 'Working Capital',
      working_capital_loan: 'Working Capital Loan',
      working_capital_loans: 'Working Capital Loan',
      commercial_real_estate: 'Commercial Real Estate',
      real_estate: 'Real Estate',
      equipment_financing: 'Equipment Financing',
      equipment_loan: 'Equipment Loan',
      equipment_loans: 'Equipment Loan',
      technical_assistance: 'Technical Assistance',
      operational_tools: 'Operational Tools',
      finding_investors: 'Finding Investors',
      solar_energy: 'Solar & Energy',
      solar_loan: 'Solar Loan',
      line_of_credit: 'Line of Credit',
      lines_of_credit: 'Lines of Credit',
      micro_loan: 'Microloan',
      micro_loans: 'Microloan',
      microloan: 'Microloan',
      term_loan: 'Term Loan',
      term_loans: 'Term Loan',
      invoice_factoring: 'Invoice Factoring',
      sba_loan: 'SBA Loan',
      sba_7a: 'SBA 7(a) Loan',
      sba_504: 'SBA 504 Loan',
      grant: 'Grant',
      grants: 'Grants',
    };

    if (knownMap[lower]) {
      return knownMap[lower];
    }

    const spaced = trimmed
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();

    return spaced
      .split(' ')
      .map((word) => {
        const wLower = word.toLowerCase();
        if (
          wLower === 'sba' ||
          wLower === 'cdfi' ||
          wLower === 'cre' ||
          wLower === 'loc'
        ) {
          return word.toUpperCase();
        }
        if (wLower === 'and' || wLower === '&') return '&';
        if (wLower === 'of') return 'of';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  getAppliedLenderName(lead: CRFAppliedLead): string {
    return lead.partner_details?.name || 'CRF Partner';
  }

  getAppliedProductImage(lead: CRFAppliedLead): string | undefined {
    if (
      lead.id !== undefined &&
      lead.id !== null &&
      this.failedAppliedImageIds.has(lead.id)
    ) {
      return undefined;
    }
    return lead.product_details?.productImage;
  }

  onAppliedImageError(lead: CRFAppliedLead): void {
    if (lead.id !== undefined && lead.id !== null) {
      this.failedAppliedImageIds.add(lead.id);
    }
  }

  getAppliedProductKind(lead: CRFAppliedLead): string {
    return (
      lead.product_details?.kind || lead.product_details?.loanType || 'Loan'
    );
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.selectedMatchForDetails) {
      this.closeDetailsModal();
    }
  }

  openDetailsModal(match: CRFMatch): void {
    this.selectedMatchForDetails = match;
  }

  openDetailsModalForLead(lead: CRFAppliedLead): void {
    const existingMatch = this.matches.find(
      (m) => this.getProductId(m) === String(lead.product_id),
    );
    if (existingMatch) {
      this.selectedMatchForDetails = existingMatch;
      return;
    }
    const details: CRFProductDetails = {
      ...(lead.product_details || {}),
      name: this.getAppliedProductName(lead),
      kind: this.getAppliedProductKind(lead),
      productImage: this.getAppliedProductImage(lead),
    };
    const partner: CRFPartnerDetails = {
      ...(lead.partner_details || {}),
      name: this.getAppliedLenderName(lead),
    };
    this.selectedMatchForDetails = {
      productId: String(lead.product_id),
      details,
      partner,
    };
  }

  closeDetailsModal(): void {
    this.selectedMatchForDetails = null;
  }

  getDetailsModalConfirmText(): string {
    if (!this.selectedMatchForDetails) {
      return 'Apply Now';
    }
    if (this.isProductApplied(this.selectedMatchForDetails)) {
      return 'Applied';
    }
    if (
      this.applyingProductId === this.getProductId(this.selectedMatchForDetails)
    ) {
      return 'Applying...';
    }
    return 'Apply Now';
  }

  isDetailsModalConfirmDisabled(): boolean {
    if (!this.selectedMatchForDetails) {
      return false;
    }
    return (
      this.isProductApplied(this.selectedMatchForDetails) ||
      this.applyingProductId !== null
    );
  }

  onDetailsModalConfirm(): void {
    if (this.selectedMatchForDetails) {
      this.applyProduct(this.selectedMatchForDetails);
    }
  }

  getTimeToFund(match: CRFMatch): string {
    const range = match.details?.timeToFundRange;
    if (!range) {
      return '';
    }
    if (range.min !== undefined && range.max !== undefined) {
      return `${range.min} - ${range.max} ${range.unit || 'Days'}`.trim();
    }
    if (range.min !== undefined) {
      return `${range.min} ${range.unit || 'Days'}`.trim();
    }
    if (range.max !== undefined) {
      return `Up to ${range.max} ${range.unit || 'Days'}`.trim();
    }
    return '';
  }

  getFeeText(fee?: CRFProductRange): string {
    if (!fee) {
      return 'None';
    }
    if (fee.unit === 'None' || (fee.min === 0 && (!fee.max || fee.max === 0))) {
      return 'None';
    }
    const unit = fee.unit ? ` (${fee.unit})` : '';
    if (fee.min !== undefined && fee.max !== undefined) {
      return `$${fee.min.toLocaleString()} - $${fee.max.toLocaleString()}${unit}`;
    }
    if (fee.min !== undefined) {
      return `$${fee.min.toLocaleString()}${unit}`;
    }
    return fee.unit || 'None';
  }

  formatLoanType(loanType?: string): string {
    if (!loanType) {
      return '';
    }
    return loanType.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  }

  goBack(): void {
    void this.router.navigate(['/business']);
  }
}
